import { useMemo } from "react";
import Fuse from "fuse.js";

/**
 * Hook de recherche fuzzy sur une liste d'items. Retourne par défaut tous les candidats sous threshold.
 *
 * @template T
 * @param {T[]} items Liste des éléments à rechercher
 * @param {string} query Texte saisi par l'utilisateur
 * @param {Object} options
 * @param {Array<string|{name: string, weight?: number}>} options.keys Champs à rechercher
 * @param {number} [options.limit] Cap post-Fuse. À éviter si un re-tri aval existe (peut éjecter
 *   des cibles pertinentes avant le re-tri).
 * @param {Object} [options.fuseOptions] Options supplémentaires transmises à Fuse
 *
 * @returns {Array<{item: T, refIndex: number, score?: number, matches?: Array}>}
 */
export const useFuzzySearch = (items, query, options = {}) => {
	const { keys = [], limit, fuseOptions = {} } = options;

	// keys et fuseOptions sont des objets : React les considère "nouveaux" à chaque render même si leur contenu n'a pas changé.
	// On les convertit en string pour que useMemo ne recrée l'index Fuse que quand le contenu change réellement.
	const keysKey = JSON.stringify(keys);
	const fuseOptionsKey = JSON.stringify(fuseOptions);

	const fuse = useMemo(() => {
		return new Fuse(items || [], {
			keys,
			threshold: 0.35,
			minMatchCharLength: 1,
			includeMatches: true,
			includeScore: true,
			ignoreLocation: true,
			// Désactive la pénalité de longueur de champ : sinon un parent court passe devant
			// une cible au lastLabel plus long à qualité de match égale.
			ignoreFieldNorm: true,
			useExtendedSearch: true,
			...fuseOptions,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items, keysKey, fuseOptionsKey]);

	const results = useMemo(() => {
		const trimmedQuery = (query || "").trim();

		if (!trimmedQuery) {
			return (items || []).map((item, refIndex) => ({ item, refIndex }));
		}

		const all = fuse.search(trimmedQuery);
		return typeof limit === "number" ? all.slice(0, limit) : all;
	}, [fuse, query, limit, items]);

	return results;
};
